import {
  isNotDefined,
  Logger,
  type DashboardDataWithEditable,
  type DashboardId,
  type DashboardListInput,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { dashboardsKey, dashboardKey } from '../keys'

const logger = new Logger('useDashboardCache')

type DashboardCache = {
  addItem: (saved: DashboardDataWithEditable) => void
  setItem: (saved: DashboardDataWithEditable) => void
  removeItem: (dashboardId: DashboardId) => void
  clearList: () => void
}

export function useDashboardCache(input?: DashboardListInput): DashboardCache {
  const queryClient = useQueryClient()
  return {
    addItem(saved: DashboardDataWithEditable) {
      queryClient.setQueryData<DashboardDataWithEditable>(dashboardKey(saved.dashboardId), saved)
      queryClient.setQueryData<DashboardDataWithEditable[]>(dashboardsKey(input), (old) => {
        if (isNotDefined(old)) return old
        return [saved, ...old]
      })
    },
    setItem(saved: DashboardDataWithEditable) {
      queryClient.setQueryData<DashboardDataWithEditable>(dashboardKey(saved.dashboardId), saved)
      queryClient.setQueriesData<DashboardDataWithEditable[]>({ queryKey: dashboardsKey() }, (old) => {
        if (isNotDefined(old)) return old
        return old.map((item) => {
          if (item.dashboardId === saved.dashboardId) {
            return saved
          }
          return item
        })
      })
    },
    removeItem(dashboardId: DashboardId) {
      queryClient.removeQueries({ queryKey: dashboardKey(dashboardId) })
      queryClient.setQueriesData<DashboardDataWithEditable[]>({ queryKey: dashboardsKey() }, (old) =>
        old?.filter((item) => item.dashboardId !== dashboardId)
      )
    },
    clearList() {
      logger.verbose('invalidateQueries dashboardListKey')
      queryClient.invalidateQueries({ queryKey: dashboardsKey(input) })
    },
  }
}
